import L from "leaflet"

const PIN_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 46" width="36" height="46">
    <path d="M18 1C9 1 2 8 2 17c0 12 16 28 16 28s16-16 16-28C34 8 27 1 18 1z" fill="#059669" stroke="#fff" stroke-width="2"/>
    <circle cx="18" cy="17" r="6" fill="#fff"/>
  </svg>`

// Free fallback map: OpenStreetMap tiles via Leaflet, address search and reverse lookup through our own server
// (which asks Nominatim / OpenCage). Used when no Google Maps key is configured or Google fails to load.
export default class LeafletProvider {
  // Suggestions are cheap to repeat, so the controller may cache them.
  cacheable = true

  constructor({ element, searchUrl, reverseUrl }) {
    this.element = element
    this.searchUrl = searchUrl
    this.reverseUrl = reverseUrl
  }

  async init({ center, zoom, marker }, { onTap, onDrag }) {
    this.onDrag = onDrag
    this.map = L.map(this.element, { scrollWheelZoom: false }).setView([center.lat, center.lng], zoom)
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map)

    this.map.on("click", (event) => onTap({ lat: event.latlng.lat, lng: event.latlng.lng }))
    this.map.on("focus", () => this.map.scrollWheelZoom.enable())
    this.map.on("blur", () => this.map.scrollWheelZoom.disable())
    if (marker) this.setMarker(marker)
  }

  setMarker(position) {
    if (this.marker) {
      this.marker.setLatLng(position)
      return
    }
    const icon = L.divIcon({ html: PIN_SVG, className: "address-pin", iconSize: [36, 46], iconAnchor: [18, 44] })
    this.marker = L.marker(position, { icon, draggable: true, autoPan: true }).addTo(this.map)
    this.marker.on("dragend", () => {
      const { lat, lng } = this.marker.getLatLng()
      this.onDrag({ lat, lng })
    })
  }

  moveTo(position, zoom) {
    this.map.setView([position.lat, position.lng], zoom)
  }

  resize() {
    this.map?.invalidateSize()
  }

  async suggest(query, { signal } = {}) {
    const response = await fetch(`${this.searchUrl}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" }, credentials: "same-origin", signal
    })
    if (!response.ok) throw new Error("search failed")
    const { results } = await response.json()
    return results.map((result) => ({
      label: result.label,
      resolve: async () => ({ lat: result.lat, lng: result.lng })
    }))
  }

  async reverse(position) {
    const response = await fetch(`${this.reverseUrl}?lat=${position.lat}&lng=${position.lng}`, {
      headers: { Accept: "application/json" }, credentials: "same-origin"
    })
    if (!response.ok) throw new Error("lookup failed")
    const { result } = await response.json()
    return result || null
  }

  destroy() {
    this.map?.remove()
    this.map = null
    this.marker = null
  }
}
